/**
 * Écran de fin de run (M2-07, complété par U-01) : niveau validé ou défaite.
 *
 * Tous les montants viennent de `POST /runs/:id/end` (`eclatsValides`, `eclatsCredites`,
 * `niveauAtteint`). En cas de défaite, le serveur renvoie `eclatsValides: 0` : l'écran affirme
 * donc explicitement qu'aucun éclat n'est versé (DECISIONS §7.1), sans jamais calculer de perte.
 *
 * Les statistiques de la run (vague atteinte, tours posées, jokers restants) sont elles aussi des
 * valeurs d'autorité : elles ne servent qu'à raconter la partie, jamais à recalculer un gain.
 *
 * **Progression de campagne (U-01)** : quand le niveau est validé et qu'un niveau suivant existe,
 * un second bouton « Niveau suivant » est proposé **à côté** de « Retour à l'accueil » — action
 * principale à gauche, retour à l'accueil en action secondaire. L'écran ne décide pas lui-même si
 * un niveau suivant existe : il affiche le bouton quand l'écran de jeu lui fournit
 * `onNiveauSuivant`, et il explique les deux cas où ce bouton manque alors que le niveau est gagné
 * (campagne terminée, niveau suivant non publié) au lieu de laisser un écran sans issue.
 *
 * Ce que l'écran ne fait **jamais** : fabriquer l'identifiant du niveau suivant, calculer un solde
 * ou supposer qu'un versement a eu lieu. Le numéro validé vient de `POST /runs/:id/end`, et le
 * solde persistant est relu au serveur par l'appelant (`GET /me`) au moment du passage.
 */
import { useTranslation } from 'react-i18next';

import { formaterNombre } from '../i18n';
import { Bouton } from './Bouton';

import type { FinDeRun } from '../jeu/reduction';

export interface ProprietesEcranFin {
  readonly fin: FinDeRun;
  /** Vague atteinte (1-based), d'après l'état d'autorité. */
  readonly vagueAtteinte: number;
  readonly totalVagues: number;
  readonly toursPosees: number;
  readonly jokersRestants: number;
  readonly onRetourAccueil: () => void;
  /**
   * Passage au niveau suivant de la campagne. Fourni **uniquement** quand un niveau suivant
   * existe dans le contenu publié (dernier niveau et manifeste incomplet exclus).
   */
  readonly onNiveauSuivant?: (() => void) | undefined;
  /** Une transition est en cours : le bouton reste affiché mais occupé (anti double-clic). */
  readonly transitionEnCours?: boolean;
  /** La dernière tentative de transition a échoué (réseau, serveur) : message affiché. */
  readonly transitionEchouee?: boolean;
  /** Le niveau validé est le dernier de la campagne du mode limité (§13.1). */
  readonly campagneTerminee?: boolean;
  /**
   * Un niveau supérieur existe dans le contenu mais le manifeste chargé ne le contient pas
   * (contenu republié pendant la partie) : on le dit au lieu de laisser un écran sans issue.
   */
  readonly niveauSuivantIndisponible?: boolean;
}

export function EcranFin({
  fin,
  vagueAtteinte,
  totalVagues,
  toursPosees,
  jokersRestants,
  onRetourAccueil,
  onNiveauSuivant,
  transitionEnCours = false,
  transitionEchouee = false,
  campagneTerminee = false,
  niveauSuivantIndisponible = false,
}: ProprietesEcranFin) {
  const { t, i18n } = useTranslation();
  const victoire = fin.raison === 'victoire';
  const nombre = (valeur: number): string => formaterNombre(valeur, i18n.language);
  const avecSuivant = onNiveauSuivant !== undefined;

  return (
    <section
      className="panneau panneau--fin"
      role="status"
      data-fin-run={victoire ? 'victoire' : 'defaite'}
    >
      <h2 className="panneau__titre">
        {victoire ? t('jeu.fin.titreVictoire') : t('jeu.fin.titreDefaite')}
      </h2>
      <p>{victoire ? t('jeu.fin.texteVictoire') : t('jeu.fin.texteDefaite')}</p>

      <dl className="hud__grille">
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.fin.niveauAtteint')}</dt>
          <dd className="hud__valeur">{nombre(fin.reponse.niveauAtteint)}</dd>
        </div>
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.fin.eclatsValides')}</dt>
          <dd className="hud__valeur">{nombre(fin.reponse.eclatsValides)}</dd>
        </div>
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.fin.eclatsVerses')}</dt>
          <dd className="hud__valeur">{nombre(fin.reponse.eclatsCredites)}</dd>
        </div>
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.fin.vagueAtteinte')}</dt>
          <dd className="hud__valeur">
            {t('jeu.hud.valeurVague', {
              numero: nombre(vagueAtteinte),
              total: nombre(totalVagues),
            })}
          </dd>
        </div>
      </dl>

      <ul className="fin-run__stats">
        <li>{t('jeu.fin.toursPosees', { nombre: nombre(toursPosees) })}</li>
        <li>{t('jeu.fin.jokersRestants', { nombre: nombre(jokersRestants) })}</li>
      </ul>

      {victoire ? null : <p className="note note--alerte">{t('jeu.fin.aucunGain')}</p>}

      {/*
        Deux actions côte à côte quand un niveau suivant existe : « Niveau suivant » en action
        principale (premier dans le DOM, donc à gauche), « Retour à l'accueil » en action
        secondaire. Sans niveau suivant (défaite, dernier niveau, contenu incomplet), le retour à
        l'accueil reste l'action principale et unique.
      */}
      <div className="fin-run__actions" data-fin-actions={avecSuivant ? 'double' : 'seule'}>
        {avecSuivant ? (
          <Bouton
            variante="principal"
            onClick={onNiveauSuivant}
            enCours={transitionEnCours}
            texteEnCours={t('jeu.fin.niveauSuivantEnCours')}
            data-action="niveau-suivant"
          >
            {t('jeu.fin.niveauSuivant')}
          </Bouton>
        ) : null}
        <Bouton
          variante={avecSuivant ? 'secondaire' : 'principal'}
          onClick={onRetourAccueil}
          disabled={transitionEnCours}
        >
          {t('commun.retourAccueil')}
        </Bouton>
      </div>

      {victoire && campagneTerminee ? (
        <p className="note" data-campagne-terminee="oui">
          {t('jeu.fin.campagneTerminee')}
        </p>
      ) : null}

      {victoire && niveauSuivantIndisponible ? (
        <p className="note note--alerte" data-niveau-suivant-indisponible="oui">
          {t('jeu.fin.niveauSuivantIndisponible')}
        </p>
      ) : null}

      {transitionEchouee ? (
        <p className="note note--alerte" role="alert" data-transition-echec="oui">
          {t('jeu.fin.niveauSuivantEchec')}
        </p>
      ) : null}
    </section>
  );
}
